/**
 * useSubsetApi - Unified Archive Subset API Hook
 *
 * Routes subset operations to the correct backend:
 * - Electron: Local via IPC (window.electronSubset)
 * - Web/Cloud: Not yet supported (Phase 4)
 *
 * Follows the useArchiveApi pattern with stable refs and memoized returns.
 *
 * @module @humanizer/studio/hooks/useSubsetApi
 */

import { useCallback, useMemo, useRef, useEffect } from 'react';
import type {
  SubsetData,
  SubsetEvaluateResult,
  SubsetBuildMappingsResult,
  SubsetSensitivityPattern,
  SubsetExportResult,
  SubsetExportJob,
} from '../types/electron.d.ts';

// Re-export types for component convenience
export type {
  SubsetData,
  SubsetEvaluateResult,
  SubsetBuildMappingsResult,
  SubsetSensitivityPattern,
  SubsetExportResult,
  SubsetExportJob,
};

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface SubsetApi {
  /** Whether subset operations are available */
  isAvailable: boolean;

  // CRUD
  create(options: {
    name: string;
    criteria: Record<string, unknown>;
    description?: string;
    exportFormat?: string;
    sharingMode?: string;
  }): Promise<SubsetData>;
  get(subsetId: string): Promise<SubsetData | null>;
  list(options?: {
    status?: string | string[];
    sharingMode?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ subsets: SubsetData[]; total: number }>;
  update(
    subsetId: string,
    options: Partial<{
      name: string;
      description: string;
      criteria: Record<string, unknown>;
      exportFormat: string;
      sharingMode: string;
    }>
  ): Promise<SubsetData | null>;
  delete(subsetId: string): Promise<boolean>;

  // Operations
  evaluate(subsetId: string): Promise<SubsetEvaluateResult>;
  buildMappings(subsetId: string): Promise<SubsetBuildMappingsResult>;
  sensitivityPatterns(): Promise<SubsetSensitivityPattern[]>;

  // Export
  exportSubset(
    subsetId: string,
    options: {
      format?: string;
      compress?: boolean;
      includeMetadata?: boolean;
      prettyPrint?: boolean;
    }
  ): Promise<SubsetExportResult>;
  exportStatus(jobId: string): Promise<SubsetExportJob | null>;

  // Progress listeners (return cleanup fns)
  onBuildMappingsProgress(
    cb: (data: { processed: number; total: number }) => void
  ): () => void;
  onExportProgress(
    cb: (data: { jobId: string; exported: number; total: number; stage: string }) => void
  ): () => void;
}

// ═══════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Hook that returns a unified subset API.
 *
 * Resolves userId internally via electronLocalAuth.getActiveProfile().
 * Electron-only for now; cloud throws "desktop-only".
 */
export function useSubsetApi(): SubsetApi {
  const isElectron = useMemo(
    () => typeof window !== 'undefined' && !!window.isElectron,
    []
  );

  // Cache userId via ref — resolved on-demand to avoid race conditions
  const userIdRef = useRef<string | null>(null);

  const getUserId = useCallback(async (): Promise<string> => {
    if (userIdRef.current) return userIdRef.current;
    if (isElectron && window.electronLocalAuth?.getActiveProfile) {
      const profile = await window.electronLocalAuth.getActiveProfile();
      userIdRef.current = profile?.id ?? null;
    }
    if (!userIdRef.current) throw new Error('No active profile. Complete onboarding first.');
    return userIdRef.current;
  }, [isElectron]);

  const getApi = useCallback(() => {
    if (!isElectron || !window.electronSubset) {
      throw new Error('Archive subsets are desktop-only. Cloud support coming in Phase 4.');
    }
    return window.electronSubset;
  }, [isElectron]);

  const isAvailable = isElectron && !!window.electronSubset;

  // CRUD
  const create = useCallback(
    async (options: {
      name: string;
      criteria: Record<string, unknown>;
      description?: string;
      exportFormat?: string;
      sharingMode?: string;
    }) => {
      const uid = await getUserId();
      return getApi().create(uid, options);
    },
    [getApi, getUserId]
  );

  const get = useCallback(
    async (subsetId: string) => {
      const uid = await getUserId();
      return getApi().get(subsetId, uid);
    },
    [getApi, getUserId]
  );

  const list = useCallback(
    async (options?: {
      status?: string | string[];
      sharingMode?: string;
      limit?: number;
      offset?: number;
    }) => {
      const uid = await getUserId();
      return getApi().list(uid, options);
    },
    [getApi, getUserId]
  );

  const update = useCallback(
    async (
      subsetId: string,
      options: Partial<{
        name: string;
        description: string;
        criteria: Record<string, unknown>;
        exportFormat: string;
        sharingMode: string;
      }>
    ) => {
      const uid = await getUserId();
      return getApi().update(subsetId, uid, options);
    },
    [getApi, getUserId]
  );

  const deleteSubset = useCallback(
    async (subsetId: string) => {
      const uid = await getUserId();
      return getApi().delete(subsetId, uid);
    },
    [getApi, getUserId]
  );

  // Operations
  const evaluate = useCallback(
    async (subsetId: string) => {
      const uid = await getUserId();
      return getApi().evaluate(subsetId, uid);
    },
    [getApi, getUserId]
  );

  const buildMappings = useCallback(
    async (subsetId: string) => {
      const uid = await getUserId();
      return getApi().buildMappings(subsetId, uid);
    },
    [getApi, getUserId]
  );

  const sensitivityPatterns = useCallback(async () => {
    return getApi().sensitivityPatterns();
  }, [getApi]);

  // Export
  const exportSubset = useCallback(
    async (
      subsetId: string,
      options: {
        format?: string;
        compress?: boolean;
        includeMetadata?: boolean;
        prettyPrint?: boolean;
      }
    ) => {
      const uid = await getUserId();
      return getApi().export(subsetId, uid, options);
    },
    [getApi, getUserId]
  );

  const exportStatus = useCallback(
    async (jobId: string) => {
      const uid = await getUserId();
      return getApi().exportStatus(jobId, uid);
    },
    [getApi, getUserId]
  );

  // Progress listeners
  const onBuildMappingsProgress = useCallback(
    (cb: (data: { processed: number; total: number }) => void) => {
      return getApi().onBuildMappingsProgress(cb);
    },
    [getApi]
  );

  const onExportProgress = useCallback(
    (cb: (data: { jobId: string; exported: number; total: number; stage: string }) => void) => {
      return getApi().onExportProgress(cb);
    },
    [getApi]
  );

  return useMemo(
    () => ({
      isAvailable,
      create,
      get,
      list,
      update,
      delete: deleteSubset,
      evaluate,
      buildMappings,
      sensitivityPatterns,
      exportSubset,
      exportStatus,
      onBuildMappingsProgress,
      onExportProgress,
    }),
    [
      isAvailable,
      create,
      get,
      list,
      update,
      deleteSubset,
      evaluate,
      buildMappings,
      sensitivityPatterns,
      exportSubset,
      exportStatus,
      onBuildMappingsProgress,
      onExportProgress,
    ]
  );
}

export default useSubsetApi;
